import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { LocalStorageService } from 'src/app/common/services/local-storage.service';
import {
  getCountryCode,
  isCountryBR,
  isCountryMX,
  isCountryPE,
  isCountryUS,
} from 'src/app/common/util/util';
import { MultiProductRoutes } from 'src/app/multi_product/mp.routes';
import { PagesRoutes } from 'src/app/pages/page.routes';
import { WizardService } from 'src/app/quote/services/wizard.service';
import { environment as env } from 'src/environments/environment';
import { QuoteRoutes } from '../../quote/quote.routes';
import { BR_CARD_DATA, MENU_CARDS, US_CARD_DATA, VIEW_ITEMS } from './menu.constant';
import { MenuInterface } from './MenuInterface';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  quoteRoutes = QuoteRoutes.ROUTES;

  pageRoutes = PagesRoutes.ROUTES;

  mpRouter = MultiProductRoutes.ROUTES;

  currentMenu = 'main';

  cardTitleEmited = '';

  country = getCountryCode();

  menu: MenuInterface = {
    title: this.checkTitleCountry(),
    subtitle: this.checkSubTitleCountry(),
    cards: [],
  };

  private cardsSubject = new Subject<MenuInterface>();

  cards$ = this.cardsSubject.asObservable();

  constructor(private router: Router, private wizardService: WizardService, private localStorageService: LocalStorageService) {}

  private appendItemsToCards(cards: Object[]): Object[] {
    const list = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const option of cards) {
      list.push(option);
    }
    return list;
  }

  checkTitleCountry(): string {
    if (env.COUNTRY?.includes('mp')) {
      if (isCountryPE()) return 'Bienvenido a Ártico Líneas No Financieras';
      if (isCountryBR()) return 'Bem-vindo Ártico linhas não financeiras';
      if (isCountryMX()) return 'Bienvenido a Ártico Líneas No Financieras';
      return 'Welcome to Ártico Líneas No Financieras';
    }
    return env.COUNTRY === 'pe'
      ? 'Para empezar escoge el producto'
      : 'Para começar, escolha uma opção';
  }

  checkSubTitleCountry(): string {
    if (env.COUNTRY?.includes('mp')) {
      if (isCountryPE()) return 'Selecciona la opción que deseas realizar';
      if (isCountryBR()) return 'Selecionar uma opção';
      if (isCountryMX()) return 'Selecciona la opción que deseas realizar';
      return 'Select the option you wish to perform';
    }
    return env.COUNTRY === 'pe' ? '¡Hola, bienvenido!' : 'Bem-vindo ao Seguro de Property!';
  }

  redirectTo(route: string, currentMenu = 'main') {
    this.currentMenu = currentMenu;
    this.router.navigate([route]);
  }

  redirectToFlowTraditional() {
    this.currentMenu = 'flow-traditional';
    this.wizardService.setWizardBackStep(
      this.pageRoutes.Flow.Traditional.Steps.StepPremisesData.index
    );
  }

  private createMainCards() {
    this.currentMenu = 'main';
    const cards = this.setCards(env.COUNTRY);
    return cards;
  }

  setCards(country: string) {
    switch (country) {
      case 'pe':
        return this.appendItemsToCards([
          MENU_CARDS.propertyInsurance,
          MENU_CARDS.engineering,
          MENU_CARDS.civilLiability,
        ]);
      case 'br':
        return this.appendItemsToCards(this.createArticoBRMenu());
      case 'mp':
      case 'mp-br':
      case 'mp-pe':
      case 'mp-mx':
      case 'mp-us':
        return this.appendItemsToCards(this.checkMpCountryCards());
      default:
        return [];
    }
  }

  checkMpCountryCards() {
    const menuItems = this.localStorageService.get('brokerSessionInfo')?.routerInfo?.elements;

    const filterMP = menuItems
      .filter(
        (item: any) => item.name.includes('ARTICO_MP_MENU') && item.name !== 'ARTICO_MP_MENU_HOME'
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);

    let cards = [];
    filterMP.forEach((e) => {
      cards.push(MENU_CARDS[e.name]);
    });

    cards.forEach((card: any) => {
      if (isCountryBR()) {
        if (!card) return;
        card.text[0] = BR_CARD_DATA[card.code].text;
        card.title = BR_CARD_DATA[card.code].title;
      } else if (isCountryUS()) {
        card.text[0] = US_CARD_DATA[card.code].text;
        card.title = US_CARD_DATA[card.code].title;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        ('');
      }
    });

    if (isCountryPE() || (isCountryMX() && cards.length === 11)) {
      cards = cards.filter((e) => e?.code !== 'MKMC');
    }
    if (!isCountryBR) {
      cards = cards.filter((e) => e.code !== 'PMMC');
    }

    return cards;
  }

  private createQuoteMenu() {
    const cards = this.appendItemsToCards([
      MENU_CARDS.quote,
      MENU_CARDS.previousQuotes,
      MENU_CARDS.policiesSold,
    ]);
    return cards;
  }

  private createEmissaoMenu() {
    const cards = this.appendItemsToCards([MENU_CARDS.flowMassive, MENU_CARDS.flowTraditional]);
    return cards;
  }

  private createArticoBRMenu() {
    const cards = this.appendItemsToCards([
      MENU_CARDS.emission,
      MENU_CARDS.attentionTray,
      MENU_CARDS.policyConsultation,
    ]);
    return cards;
  }

  private createArticoEngineeringMenu() {
    const cards = this.appendItemsToCards([
      MENU_CARDS.emissionEngineering,
      MENU_CARDS.attentionEngineeringTray,
      MENU_CARDS.policyConsultationEngineering,
    ]);
    return cards;
  }

  getCurrentMenu() {
    return this.currentMenu;
  }

  getCurrentEmitedCard() {
    return this.cardTitleEmited;
  }

  setCurrentMenu(current: string) {
    this.currentMenu = current;
  }

  getIsInMainMenu(): boolean {
    return this.currentMenu === 'main';
  }

  changeViewItems(itemTitle: string) {
    let cards = [];
    if (itemTitle === '') {
      this.setCurrentMenu('main');
      cards = this.createMainCards();
      this.menu.cards = cards;
      this.menu.title = this.checkTitleCountry();
      this.menu.subtitle = this.checkSubTitleCountry();
      this.cardsSubject.next(this.menu);
    }

    if (itemTitle === 'Propiedad') {
      this.setCurrentMenu('quote');
      this.menu.cards = this.createQuoteMenu();
      this.menu.title = 'Seguro de Propiedad';
      this.menu.subtitle = '¿Qué deseas realizar?';
      this.cardsSubject.next(this.menu);
    }

    if (itemTitle === 'Emissão') {
      this.setCurrentMenu('Emissão');
      this.menu.cards = this.createEmissaoMenu();
      this.menu.title = 'Que tipo de emissão você quer fazer?';
      this.cardsSubject.next(this.menu);
    }

    if (VIEW_ITEMS[itemTitle]) {
      this.redirectTo(VIEW_ITEMS[itemTitle].route, VIEW_ITEMS[itemTitle].menu);
    }

    if (itemTitle === 'Emissão tradicional') {
      this.redirectToFlowTraditional();
    }

    if (itemTitle === 'Seguro de Property') {
      this.setCurrentMenu('Seguro de Property');
      this.menu.cards = this.createArticoBRMenu();
      this.menu.title = 'Bem-vindo ao Seguro de Property!';
      this.cardsSubject.next(this.menu);
    }

    if (itemTitle === 'Seguro de Engenharia') {
      this.setCurrentMenu('Seguro de Engenharia');
      this.menu.cards = this.createArticoEngineeringMenu();
      this.menu.title = 'Seguro de Engenharia';
      this.cardsSubject.next(this.menu);
    }
  }
}
